import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Portfolio, PortfolioEntry, ETF, AddPortfolioEntryParams, IsinLookupResult } from '../../services/portfolio';
import { CHART_COLORS, useIsMobile } from '../../utils/chartHelpers';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { formatPercentage } from '../../utils/formatters';
import AddETFModal from './modals/AddETFModal';
import EditETFModal from './modals/EditETFModal';
import DeleteConfirmationModal from './modals/DeleteConfirmationModal';
import ChartContainer from '../ui/ChartContainer';

interface ETFInvestmentsSectionProps {
  portfolio: Portfolio;
  etfs: ETF[] | undefined;
  totalEtfValue: number;
  etfAllocationData: Array<{ name: string; value: number; fullName: string }>;

  // Modal state
  showAddModal: boolean;
  setShowAddModal: (show: boolean) => void;
  showEditModal: boolean;
  setShowEditModal: (show: boolean) => void;
  showDeleteConfirm: boolean;
  setShowDeleteConfirm: (show: boolean) => void;
  selectedEntry: PortfolioEntry | null;
  setSelectedEntry: (entry: PortfolioEntry | null) => void;

  // Form state
  etfSearchTerm: string;
  setEtfSearchTerm: (term: string) => void;
  formData: AddPortfolioEntryParams;
  setFormData: React.Dispatch<React.SetStateAction<AddPortfolioEntryParams>>;

  // Mutations
  addEntryMutation: {
    mutate: (data: AddPortfolioEntryParams) => void;
    isPending: boolean;
  };
  updateEntryMutation: {
    mutate: (data: { entryId: number; data: Partial<AddPortfolioEntryParams> }) => void;
    isPending: boolean;
  };
  removeEntryMutation: {
    mutate: (entryId: number) => void;
    isPending: boolean;
  };

  // Event handlers
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handleAddSubmit: (e: React.FormEvent) => void;
  handleEditSubmit: (e: React.FormEvent) => void;
  handleDelete: () => void;
  resetForm: () => void;
  openEditModal: (entry: PortfolioEntry) => void;
  openDeleteConfirm: (entry: PortfolioEntry) => void;

  // ISIN lookup
  isinLookup: {
    isin: string; setIsin: (v: string) => void;
    resolved: IsinLookupResult | null; resolveError: string | null;
    shares: string; setShares: (v: string) => void;
    purchasePrice: string; setPurchasePrice: (v: string) => void;
    purchaseDate: string; setPurchaseDate: (v: string) => void;
    purchaseCurrency: string; setPurchaseCurrency: (v: string) => void;
    handleResolve: () => void; handleSubmit: (e: React.FormEvent) => void;
    clearResolved: () => void; reset: () => void;
    isResolving: boolean; isSubmitting: boolean; addError: any;
  };
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #f4f4f5',
      borderRadius: '8px',
      padding: '8px 12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      fontSize: '0.8rem',
    }}>
      <div style={{ fontWeight: 600, color: '#18181b', marginBottom: '2px' }}>{payload[0].name}</div>
      <div style={{ color: '#71717a' }}>{payload[0].payload?.fullName}</div>
    </div>
  );
};

const iconEdit = (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#71717a" viewBox="0 0 16 16">
    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
  </svg>
);

const iconDelete = (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#ef4444" viewBox="0 0 16 16">
    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
    <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3h11V2h-11v1Z"/>
  </svg>
);

const ETFInvestmentsSection: React.FC<ETFInvestmentsSectionProps> = ({
  portfolio,
  etfs,
  totalEtfValue,
  etfAllocationData,
  showAddModal,
  setShowAddModal,
  showEditModal,
  setShowEditModal,
  showDeleteConfirm,
  setShowDeleteConfirm,
  selectedEntry,
  setSelectedEntry,
  etfSearchTerm,
  setEtfSearchTerm,
  formData,
  setFormData,
  addEntryMutation,
  updateEntryMutation,
  removeEntryMutation,
  handleInputChange,
  handleAddSubmit,
  handleEditSubmit,
  handleDelete,
  resetForm,
  openEditModal,
  openDeleteConfirm,
  isinLookup,
}) => {
  const { isPrivateMode } = usePrivacy();
  const { formatAmount } = useCurrency();
  const isMobile = useIsMobile();

  const legendConfig = isMobile ? null : {
    layout: 'horizontal' as const,
    wrapperStyle: { paddingTop: '12px', fontSize: '12px', color: '#71717a' },
  };

  const gridCols = '1fr 80px 90px 110px 110px 60px 90px 64px';

  return (
    <>
      <div className="row g-3 mb-4">
      {/* ETF allocation chart */}
      <div className={`col-12 ${portfolio.entries?.length > 0 ? 'col-lg-4' : ''}`}>
      <div className="card card-forest h-100">
        <div className="card-body">
          <div className="mb-3">
            <span className="fw-semibold" style={{ color: '#000537', fontSize: '0.95rem' }}>ETF Allocation</span>
          </div>
          {etfAllocationData.length > 0 ? (
            <ChartContainer size="md">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={etfAllocationData}
                    cx="50%" cy="50%"
                    innerRadius="40%"
                    outerRadius="68%"
                    paddingAngle={2}
                    dataKey="value"
                    labelLine={false}
                  >
                    {etfAllocationData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  {legendConfig && (
                    <Legend
                      formatter={(value) => {
                        const item = etfAllocationData.find(i => i.name === value);
                        const pct = item ? ((item.value / totalEtfValue) * 100).toFixed(1) : '0.0';
                        return <span style={{ color: '#52525b', fontSize: '12px' }}>{value} ({pct}%)</span>;
                      }}
                      {...legendConfig}
                    />
                  )}
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          ) : (
            <div className="d-flex align-items-center justify-content-center py-5">
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="currentColor" viewBox="0 0 16 16" style={{ color: '#d4d4d8', display: 'block', margin: '0 auto 12px' }}>
                  <path d="M7.5 1.018a7 7 0 0 0-4.79 11.566L7.5 7.793V1.018zm1 0V7.5h6.482A7.001 7.001 0 0 0 8.5 1.018zM14.982 8.5H8.207l-4.79 4.79A7 7 0 0 0 14.982 8.5zM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8z"/>
                </svg>
                <div style={{ fontSize: '0.875rem', color: '#71717a', marginBottom: '12px' }}>No ETFs in this portfolio</div>
                <button onClick={() => setShowAddModal(true)} className="btn btn-primary btn-sm">Add your first ETF</button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>{/* col chart */}

      {/* ETF holdings table */}
      {portfolio.entries && portfolio.entries.length > 0 && (
        <div className="col-12 col-lg-8">
        <div className="card h-100" style={{ border: '1px solid #f4f4f5', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f4f4f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="fw-semibold" style={{ color: '#000537', fontSize: '0.95rem' }}>ETF Holdings</span>
              <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>{portfolio.entries.length} positions</span>
            </div>
            <button onClick={() => setShowAddModal(true)} className="btn btn-primary btn-sm">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="me-1" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2Z"/>
              </svg>
              Add ETF
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}><div style={{ minWidth: 720 }}>
          {/* Header row */}
          <div style={{
            padding: '10px 20px',
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: '12px',
            borderBottom: '1px solid #f4f4f5',
          }}>
            <span className="section-label">ETF</span>
            <span className="section-label">Shares</span>
            <span className="section-label">Buy Price</span>
            <span className="section-label">Cur. Price</span>
            <span className="section-label">Cur. Value</span>
            <span className="section-label">Weight</span>
            <span className="section-label">Date</span>
            <span className="section-label"></span>
          </div>

          {portfolio.entries.map((entry, idx) => {
            const currentValue = Number(entry.shares) * (entry.current_price ? Number(entry.current_price) : 0);
            const weight = totalEtfValue > 0 ? (currentValue / totalEtfValue) * 100 : 0;

            return (
              <div
                key={entry.id}
                style={{
                  padding: '11px 20px',
                  display: 'grid',
                  gridTemplateColumns: gridCols,
                  gap: '12px',
                  alignItems: 'center',
                  borderBottom: idx < portfolio.entries.length - 1 ? '1px solid #f4f4f5' : 'none',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#fafafa')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#18181b', fontFamily: 'monospace' }}>{entry.etf.ticker}</div>
                  <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '1px' }}>{entry.etf.name}</div>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>
                  {isPrivateMode ? '***' : entry.shares.toFixed(2)}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>
                  {formatAmount(entry.purchase_price || 0, isPrivateMode)}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>
                  {formatAmount(entry.current_price || 0, isPrivateMode)}
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#000537', fontVariantNumeric: 'tabular-nums' }}>
                  {formatAmount(currentValue, isPrivateMode)}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#52525b', fontVariantNumeric: 'tabular-nums' }}>
                  {formatPercentage(weight)}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#71717a' }}>
                  {entry.purchase_date ? new Date(entry.purchase_date).toLocaleDateString() : <span style={{ color: '#d4d4d8' }}>—</span>}
                </div>
                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => openEditModal(entry)}
                    style={{ width: '28px', height: '28px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e4e4e7', borderRadius: '6px', background: 'transparent', cursor: 'pointer' }}
                    title="Edit"
                  >
                    {iconEdit}
                  </button>
                  <button
                    onClick={() => openDeleteConfirm(entry)}
                    style={{ width: '28px', height: '28px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #fee2e2', borderRadius: '6px', background: 'transparent', cursor: 'pointer' }}
                    title="Remove"
                  >
                    {iconDelete}
                  </button>
                </div>
              </div>
            );
          })}
          </div></div>{/* scroll wrapper */}
        </div>
        </div>
      )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddETFModal
          etfs={etfs}
          etfSearchTerm={etfSearchTerm}
          setEtfSearchTerm={setEtfSearchTerm}
          formData={formData}
          handleInputChange={handleInputChange}
          handleAddSubmit={handleAddSubmit}
          setShowAddModal={setShowAddModal}
          resetForm={resetForm}
          isPending={addEntryMutation.isPending}
          isinLookup={isinLookup}
        />
      )}

      {showEditModal && selectedEntry && (
        <EditETFModal
          selectedEntry={selectedEntry}
          formData={formData}
          handleInputChange={handleInputChange}
          handleEditSubmit={handleEditSubmit}
          setShowEditModal={setShowEditModal}
          resetForm={resetForm}
          isPending={updateEntryMutation.isPending}
        />
      )}

      {showDeleteConfirm && selectedEntry && (
        <DeleteConfirmationModal
          itemName={selectedEntry.etf.ticker}
          handleDelete={handleDelete}
          setShowDeleteConfirm={setShowDeleteConfirm}
          resetSelection={() => setSelectedEntry(null)}
          isPending={removeEntryMutation.isPending}
          itemType="ETF"
        />
      )}
    </>
  );
};

export default ETFInvestmentsSection;
